import { type JSX, type ReactNode, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { Select, SelectItem } from '@heroui/react';
import clsx from 'clsx';
import { CheckCircle2, Loader2, Zap } from 'lucide-react';

import { useSetPageChrome } from '~/contexts/PageChromeContext';
import { useDashboardCompleted, useDashboardInProgress } from '~/hooks/useProducts';

import { MAIN_CONTENT_WIDTH } from '~/components/layout/mainContent';

import { DashboardProjectCard } from './DashboardProjectCard';
import {
  RECENTLY_COMPLETED_LIMIT,
  selectInProgressProjects,
  selectRecentlyCompleted,
  TIME_FILTER_OPTIONS,
} from './dashboardSelectors';
import type { ProductProjectEntry, TimeFilterValue } from './dashboardSelectors';
import { DashboardSkeleton } from './DashboardSkeleton';
import { usePersistedCollapsed, usePersistedTimeFilter } from './useDashboardPersistedState';

/** Stable reference — the Dashboard has a fixed title and no breadcrumb trail. */
const DASHBOARD_CHROME = { title: 'Dashboard' };

// Independent localStorage keys per section, per persisted setting — the two
// sections' windows and collapsed states must never share state.
const IN_PROGRESS_WINDOW_STORAGE_KEY = 'dashboard-in-progress-window';
const COMPLETED_WINDOW_STORAGE_KEY = 'dashboard-completed-window';
const IN_PROGRESS_COLLAPSED_STORAGE_KEY = 'dashboard-in-progress-collapsed';
const COMPLETED_COLLAPSED_STORAGE_KEY = 'dashboard-completed-collapsed';

/**
 * A section's collapsible body — height-animated via `grid-template-rows`
 * (0fr/1fr), matching `Accordion.tsx`'s transition exactly (150ms ease-out),
 * rather than introducing a new animation technique.
 */
function CollapsibleBody({
  bodyId,
  children,
  collapsed,
}: {
  bodyId: string;
  children: ReactNode;
  collapsed: boolean;
}): JSX.Element {
  return (
    <div
      className={clsx('grid transition-[grid-template-rows] duration-150 ease-out', {
        'grid-rows-[0fr]': collapsed,
        'grid-rows-[1fr]': !collapsed,
      })}
      id={bodyId}
    >
      <div className="overflow-hidden">{children}</div>
    </div>
  );
}

/**
 * Small inline "refreshing" indicator shown beside a dropdown while a
 * background refetch is in flight (driven by `isFetching`, not `isLoading`,
 * so it never replaces the still-visible cards with a full skeleton).
 * Pattern: docs/design-patterns/frontend/component-patterns.md (loading-indicator-accessibility)
 */
function FetchingIndicator(): JSX.Element {
  return (
    <span className="inline-flex items-center" role="status">
      <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin text-muted" />
      <span className="sr-only">Updating…</span>
    </span>
  );
}

/**
 * In Progress's empty-state copy is conditional on the active filter: at the
 * unbounded 'all' option there's nothing wider to suggest trying, but any
 * bounded window (including the 'week' default) may still be hiding other
 * in-progress work outside it.
 */
function InProgressEmptyState({ timeFilter }: { timeFilter: TimeFilterValue }): JSX.Element {
  const activeOption = TIME_FILTER_OPTIONS.find(option => option.value === timeFilter);
  const isUnbounded = timeFilter === 'all';

  return (
    <div className="flex flex-col items-center rounded-lg border border-dashed border-default p-8 text-center">
      <Zap aria-hidden="true" className="mb-3 h-12 w-12 text-muted opacity-40" />
      <p className="text-sm text-muted">
        {isUnbounded ? (
          'Nothing in progress right now.'
        ) : (
          <>
            Nothing in progress was updated in {toWindowLabel(activeOption?.label ?? '')}. Try a
            wider time range, or
          </>
        )}{' '}
        <Link className="text-primary hover:underline" to="/products">
          View products
        </Link>
      </p>
    </div>
  );
}

/**
 * Renders a responsive grid of project cards from a flat list of
 * product+project entries — replaces the old per-product `ProductGroupList`
 * (`DashboardProductGroup` container) now that cards are peers, not grouped.
 *
 * Pattern: docs/design-patterns/frontend/component-patterns.md (display-components-props-only)
 * Pattern: docs/design-patterns/frontend/responsive-design.md (standard-breakpoints)
 */
function ProjectCardGrid({ entries }: { entries: ProductProjectEntry[] }): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {entries.map(entry => (
        <DashboardProjectCard
          key={entry.project.id}
          productName={entry.product.name}
          project={entry.project}
        />
      ))}
    </div>
  );
}

/**
 * Recently Completed's empty-state copy mirrors `InProgressEmptyState`'s
 * two-copy pattern: "true-empty" (the org has never completed a project,
 * i.e. the window is 'all' and still empty) vs "narrowed-empty" (completions
 * exist, just none within the currently selected bounded window).
 */
function RecentlyCompletedEmptyState({
  completedWindow,
}: {
  completedWindow: TimeFilterValue;
}): JSX.Element {
  const activeOption = TIME_FILTER_OPTIONS.find(option => option.value === completedWindow);
  const isUnbounded = completedWindow === 'all';

  return (
    <div className="flex flex-col items-center rounded-lg border border-dashed border-default p-8 text-center">
      <CheckCircle2 aria-hidden="true" className="mb-3 h-12 w-12 text-muted opacity-40" />
      <p className="text-sm text-muted">
        {isUnbounded ? (
          'Nothing completed yet.'
        ) : (
          <>
            Nothing completed in {toWindowLabel(activeOption?.label ?? '')}. Try a wider time range,
            or
          </>
        )}{' '}
        <Link className="text-primary hover:underline" to="/products">
          View products
        </Link>
      </p>
    </div>
  );
}

/**
 * A section's header row: a collapse-toggle button (chevron + heading —
 * never wraps the `Select`, which stays a sibling; nesting a `<select>`
 * inside a `<button>` is invalid HTML) plus the time-filter dropdown, in the
 * same `flex-col sm:flex-row` row used by both sections identically.
 */
function SectionHeader({
  bodyId,
  collapsed,
  filterLabel,
  headingClassName,
  isFetching,
  onSelectionChange,
  onToggleCollapsed,
  title,
  window: selectedWindow,
}: {
  bodyId: string;
  collapsed: boolean;
  filterLabel: string;
  headingClassName: string;
  isFetching: boolean;
  onSelectionChange: (value: TimeFilterValue) => void;
  onToggleCollapsed: () => void;
  title: string;
  window: TimeFilterValue;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <button
        aria-controls={bodyId}
        aria-expanded={!collapsed}
        className="flex items-center gap-2 text-left"
        type="button"
        onClick={onToggleCollapsed}
      >
        <ChevronRightIcon
          aria-hidden="true"
          className={clsx(
            'h-5 w-5 shrink-0 text-muted transition-transform duration-150 ease-out',
            { 'rotate-90': !collapsed },
          )}
        />
        <h2 className={headingClassName}>{title}</h2>
      </button>
      <div className="flex items-center gap-2">
        <Select
          aria-label={filterLabel}
          className="w-full sm:w-56"
          disallowEmptySelection
          selectedKeys={[selectedWindow]}
          size="sm"
          onSelectionChange={keys => onSelectionChange([...keys][0] as TimeFilterValue)}
        >
          {TIME_FILTER_OPTIONS.map(option => (
            <SelectItem key={option.value}>{option.label}</SelectItem>
          ))}
        </Select>
        {isFetching && <FetchingIndicator />}
      </div>
    </div>
  );
}

/** "Last 24 hours" -> "the last 24 hours", for inline empty-state/subtitle copy. */
function toWindowLabel(optionLabel: string): string {
  return `the ${optionLabel.charAt(0).toLowerCase()}${optionLabel.slice(1)}`;
}

/**
 * /dashboard — two independent, always-visible-unless-collapsed sections:
 * every `IN_PROGRESS` project within its own server-side time window, and
 * the most recently completed projects within a second, independent
 * server-side time window. Both windows and both collapsed states persist to
 * `localStorage` per section (`usePersistedTimeFilter`/`usePersistedCollapsed`).
 *
 * Each section's dropdown re-keys only that section's own `useQuery`
 * (`useDashboardInProgress`/`useDashboardCompleted`) — a filter change
 * refetches and flashes only the section it belongs to, not both.
 * `anti-patterns/request-fan-out.md`'s actual rule is request count staying
 * constant regardless of list size, not "zero requests after any
 * interaction" — two independently re-keyed queries satisfy it (see
 * spec.md Product Decision 1).
 *
 * Container — owns data fetching (two independent queries), both windows,
 * both collapsed states, and both selector derivations;
 * DashboardSkeleton/ProjectCardGrid/DashboardProjectCard are display-only.
 * Pattern: docs/design-patterns/frontend/component-patterns.md
 */
export function Dashboard(): JSX.Element {
  useSetPageChrome(DASHBOARD_CHROME);

  const [inProgressWindow, setInProgressWindow] = usePersistedTimeFilter(
    IN_PROGRESS_WINDOW_STORAGE_KEY,
  );
  const [completedWindow, setCompletedWindow] = usePersistedTimeFilter(
    COMPLETED_WINDOW_STORAGE_KEY,
  );
  const [inProgressCollapsed, toggleInProgressCollapsed] = usePersistedCollapsed(
    IN_PROGRESS_COLLAPSED_STORAGE_KEY,
  );
  const [completedCollapsed, toggleCompletedCollapsed] = usePersistedCollapsed(
    COMPLETED_COLLAPSED_STORAGE_KEY,
  );

  const inProgressQuery = useDashboardInProgress(inProgressWindow);
  const completedQuery = useDashboardCompleted(completedWindow);

  const isLoading = inProgressQuery.isLoading || completedQuery.isLoading;
  const isError = inProgressQuery.isError || completedQuery.isError;

  const inProgressEntries = useMemo(
    () => selectInProgressProjects(inProgressQuery.data ?? []),
    [inProgressQuery.data],
  );
  const recentlyCompletedEntries = useMemo(
    () => selectRecentlyCompleted(completedQuery.data ?? []),
    [completedQuery.data],
  );

  const completedWindowLabel =
    TIME_FILTER_OPTIONS.find(option => option.value === completedWindow)?.label ?? '';
  const completedSubtitle =
    completedWindow === 'all'
      ? `Showing up to ${RECENTLY_COMPLETED_LIMIT} most recently completed`
      : `Showing up to ${RECENTLY_COMPLETED_LIMIT} most recently completed in ${toWindowLabel(completedWindowLabel)}`;

  return (
    <div className={`space-y-8 p-4 ${MAIN_CONTENT_WIDTH}`}>
      <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
      {isLoading && <DashboardSkeleton />}
      {isError && (
        <p className="text-sm text-error" role="alert">
          Failed to load dashboard. Please try again later.
        </p>
      )}

      {!isLoading && !isError && (
        <>
          <section className="space-y-3">
            <SectionHeader
              bodyId="dashboard-in-progress-body"
              collapsed={inProgressCollapsed}
              filterLabel="Filter in-progress projects by time"
              headingClassName="text-xl font-semibold text-foreground"
              isFetching={inProgressQuery.isFetching}
              title="In Progress Projects"
              window={inProgressWindow}
              onSelectionChange={setInProgressWindow}
              onToggleCollapsed={toggleInProgressCollapsed}
            />
            <CollapsibleBody bodyId="dashboard-in-progress-body" collapsed={inProgressCollapsed}>
              {inProgressEntries.length === 0 ? (
                <InProgressEmptyState timeFilter={inProgressWindow} />
              ) : (
                <ProjectCardGrid entries={inProgressEntries} />
              )}
            </CollapsibleBody>
          </section>

          <section className="space-y-3">
            <SectionHeader
              bodyId="dashboard-recently-completed-body"
              collapsed={completedCollapsed}
              filterLabel="Filter recently completed projects by time"
              headingClassName="text-lg font-semibold text-foreground"
              isFetching={completedQuery.isFetching}
              title="Recently Completed Projects"
              window={completedWindow}
              onSelectionChange={setCompletedWindow}
              onToggleCollapsed={toggleCompletedCollapsed}
            />
            <CollapsibleBody
              bodyId="dashboard-recently-completed-body"
              collapsed={completedCollapsed}
            >
              <div className="space-y-3">
                <p className="text-xs text-muted">{completedSubtitle}</p>
                {recentlyCompletedEntries.length === 0 ? (
                  <RecentlyCompletedEmptyState completedWindow={completedWindow} />
                ) : (
                  <ProjectCardGrid entries={recentlyCompletedEntries} />
                )}
              </div>
            </CollapsibleBody>
          </section>
        </>
      )}
    </div>
  );
}
